import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Checkbox,
} from "@mui/material";
import {
  Search as SearchIcon,
  ArrowBack,
  Download as DownloadIcon
} from "@mui/icons-material";
import moment from "moment";
import { Link } from "@mui/material";
import currency from "currency.js";

import { PERMISSIONS } from "../../constants";
import { getContracts } from "../../services/backend/ContractService";
import { exportContractsToCSV, getContractField, getStatusColor } from "../../functions";

const CONTRACT_STATUS = {
  DRAFT: 'BORRADOR',
  SUPERVISOR: 'SUPERVISOR',
  APPROVED: 'APROBADO',
  CLOSED: 'CERRADO',
  SUSPENDED: 'SUSPENDIDO',
  FINISHED: 'TERMINADO',
};

const ContractConsolidate = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);

  const getContractsAsync = async () => {
    setLoading(true);
    try {
      const response = await getContracts(user);
      const allContracts = response.map((contract) => ({
        ...contract,
        approvedAt: contract.approvedAt ? moment(contract.approvedAt) : null,
        finishedAt: contract.finishedAt ? moment(contract.finishedAt) : null,
        startedAt: contract.startedAt ? moment(contract.startedAt) : null,
        createdAt: contract.createdAt ? moment(contract.createdAt) : null,
        updatedAt: contract.updatedAt ? moment(contract.updatedAt) : null,
        news: contract.news?.map(n => ({
          ...n,
          startedAt: n.startedAt ? moment(n.startedAt) : null,
          finishedAt: n.finishedAt ? moment(n.finishedAt) : null,
        }))
      }));
      
      setContracts(allContracts || []);
      setFilteredContracts(allContracts || []);
    } catch (error) {
      console.error('Error loading contracts:', error);
      setContracts([]);
      setFilteredContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term, status) => {
    let filtered = [...contracts];

    // Filter by status
    if (status !== 'ALL') {
      filtered = filtered.filter(contract => contract.status === status);
    }

    // Filter by search term
    if (term) {
      const searchLower = term.toLowerCase();
      filtered = filtered.filter(contract => 
        contract.contractCode?.toLowerCase().includes(searchLower) ||
        contract.contractSubject?.toLowerCase().includes(searchLower) ||
        contract.contractor?.name?.toLowerCase().includes(searchLower) ||
        contract.contractor?.lastName?.toLowerCase().includes(searchLower) ||
        contract.contractorNuip?.toString().includes(searchLower) ||
        contract.supervisors?.some(s => 
          s.name?.toLowerCase().includes(searchLower) ||
          s.lastName?.toLowerCase().includes(searchLower)
        )
      );
    }

    setFilteredContracts(filtered);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    handleSearch(value, statusFilter);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    handleSearch(searchTerm, value);
  };

  useEffect(() => {
    if (!user.permissions.includes(PERMISSIONS.CONTRACT.CONSOLIDATE)) {
      navigate('/');
      return;
    }

    getContractsAsync();
  }, [user, navigate]); // eslint-disable-line

  return (
    <Container maxWidth="lg" style={{ padding: 3, marginTop: 10 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          style={{ margin: 16 }}
          onClick={() => navigate('/')}
        >
          Volver
        </Button>
        Consolidado de Contratos
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por código, objeto, contratista, supervisor o cédula..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Estado</InputLabel>
            <Select
              value={statusFilter}
              label="Estado"
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <MenuItem value="ALL">Todos los estados</MenuItem>
              {Object.values(CONTRACT_STATUS).map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Summary */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          label={`Total: ${filteredContracts.length}`}
          color="primary"
        />
        <Chip
          label={`Valor Total: ${currency(filteredContracts.reduce((sum, c) => {
            const getField = getContractField(c.news || [], c);
            return sum + (getField('contractBudget') || 0);
          }, 0)).format()}`}
          color="success"
        />
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => exportContractsToCSV(filteredContracts)}
          size="small"
          sx={{ ml: 'auto' }}
        >
          Descargar CSV
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 350px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Contratista</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Cédula</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Correo</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Supervisor(es)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Objeto</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right" rowSpan={2}>Valor</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="center" rowSpan={2}>Duración</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="center" rowSpan={2}>F. Inicio</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="center" rowSpan={2}>F. Cierre</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Dependencia</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} rowSpan={2}>Naturaleza</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="center" rowSpan={2}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} colSpan={3} align="center">Novedades</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} colSpan={2} align="center">Anexos</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8e8e8', fontSize: '0.7rem', py: 0.5 }} align="center">Tipo</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8e8e8', fontSize: '0.7rem', py: 0.5 }} align="center">F. Inicio</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8e8e8', fontSize: '0.7rem', py: 0.5 }} align="center">F. Fin</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8e8e8', fontSize: '0.7rem', py: 0.5 }} align="center">SECOP</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8e8e8', fontSize: '0.7rem', py: 0.5 }} align="center">CDP/CRP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={18} align="center" sx={{ py: 4 }}>
                  Cargando contratos...
                </TableCell>
              </TableRow>
            ) : filteredContracts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={18} align="center" sx={{ py: 4 }}>
                  No se encontraron contratos
                </TableCell>
              </TableRow>
            ) : (
              filteredContracts.map((contract, index) => {
                const getContractFieldWithNews = getContractField(
                  contract.news || [],
                  {
                    status: contract.status,
                    finishedAt: contract.finishedAt,
                    contractPeriod: contract.contractPeriod,
                    contractBudget: contract.contractBudget,
                    paymentPlan: contract.paymentPlan,
                    startedAt: contract.startedAt,
                    supervisors: contract.supervisors,
                    salary: contract.salary,
                    tasks: contract.tasks
                  }
                );

                return (
                <TableRow 
                  key={contract.contractId || index}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                  }}
                >
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate(`/contracts/${contract.contractCode}`)}
                      target="_blank"
                      sx={{ 
                        fontWeight: 500,
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {contract.contractCode || '--'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {contract.contractType || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {contract.contractor 
                        ? `${contract.contractor.name || ''} ${contract.contractor.lastName || ''}`
                        : 'Sin asignar'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {contract.contractorNuip || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {contract.contractor?.email || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ maxWidth: 200 }}>
                      {contract.supervisors?.length > 0 ? (
                        contract.supervisors.map((sup, i) => (
                          <Typography key={i} variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {`${sup.name || ''} ${sup.lastName || ''}`}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">--</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={contract.contractSubject || ''}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.75rem'
                        }}
                      >
                        {contract.contractSubject || '--'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {currency(getContractFieldWithNews('contractBudget')).format()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {(() => {
                        const period = getContractFieldWithNews('contractPeriod');
                        return period ? `${period} días` : '--';
                      })()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {contract.startedAt 
                        ? moment(contract.startedAt.toDate ? contract.startedAt.toDate() : contract.startedAt).format('DD/MM/YYYY')
                        : '--'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {(() => {
                        const finishedAt = getContractFieldWithNews('finishedAt');
                        return finishedAt
                          ? moment(finishedAt.toDate ? finishedAt.toDate() : finishedAt).format('DD/MM/YYYY')
                          : '--';
                      })()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {contract.dependency || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {contract.nature || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={contract.status || '--'}
                      color={getStatusColor(contract.status)}
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    {contract.news && contract.news.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {contract.news.map((news, idx) => (
                          <Typography key={idx} variant="body2" sx={{ fontSize: '0.7rem' }}>
                            {news.type || '--'}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>--</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {contract.news && contract.news.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {contract.news.map((news, idx) => (
                          <Typography key={idx} variant="body2" sx={{ fontSize: '0.7rem' }}>
                            {news.startedAt 
                              ? moment(news.startedAt.toDate ? news.startedAt.toDate() : news.startedAt).format('DD/MM/YYYY')
                              : '--'
                            }
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>--</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {contract.news && contract.news.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {contract.news.map((news, idx) => (
                          <Typography key={idx} variant="body2" sx={{ fontSize: '0.7rem' }}>
                            {news.finishedAt 
                              ? moment(news.finishedAt.toDate ? news.finishedAt.toDate() : news.finishedAt).format('DD/MM/YYYY')
                              : '--'
                            }
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={contract.annexes?.some(a => a.name?.toLowerCase().includes("secop")) || false}
                      disabled
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={contract.annexes?.some(a => a.name?.toLowerCase().includes("cdp y crp")) || false}
                      disabled
                      size="small"
                    />
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default ContractConsolidate;
